import type { ReactElement } from "react";
import { Outlet, type RouteObject } from "react-router";
import { CustomerDetailPage } from "../../features/customers/customerDetailPage.js";
import { CustomerNameCrumbLabel } from "../../features/customers/customerNameCrumbLabel.js";
import { CustomerProfileGuard } from "../../features/customers/customerProfileGuard.js";
import { CustomersPage } from "../../features/customers/customersPage.js";
import { TrainingTeammatePageAchievements } from "../../features/teammatePerformance/trainingTeammatePageAchievements.js";
import { TrainingTeammatePageAutomationResults } from "../../features/teammatePerformance/trainingTeammatePageAutomationResults.js";
import { TrainingTeammatePageCalls } from "../../features/teammatePerformance/trainingTeammatePageCalls.js";
import { TrainingTeammatePageOverview } from "../../features/teammatePerformance/trainingTeammatePageOverview.js";
import { TrainingTeammatePageTraining } from "../../features/teammatePerformance/trainingTeammatePageTraining.js";
import { TrainingTeammatePage } from "../../pages/trainingTeammatePage.js";
import { appPaths } from "../appPaths.js";
import { defineHandle } from "../navigation/appRouteHandle.js";
import type { BuildCrumbs } from "../navigation/breadcrumbs.js";
import { PerformanceNavigationMenu } from "../navigation/menu/performanceNavigationMenu.js";
import { performanceSections } from "../navigation/performanceSections.js";
import { WorkspaceId } from "../navigation/workspaces.js";
import { requireRouteParam } from "../routeParams.js";
import { GradingTupleRedirect, ParamsRedirect } from "./compatRedirects.js";
import {
  createCriterionDrilldownRoute,
  createGradingSlideoverRoute,
} from "./gradingRoutes.js";

type WrapElement = (
  element: ReactElement,
  options: { withNavigationFallback: boolean },
) => ReactElement;

// The team-member-only Notifications list tab. The focused single-result detail
// is hoisted to a standalone route (see routeTrees) so it renders as its own
// titled page instead of under the teammate tab header.
function createAutomationResultsRoute(wrapElement: WrapElement): RouteObject {
  return {
    path: "automation-results",
    handle: defineHandle({
      crumbs: ({ pathname }) => [{ label: "Notifications", href: pathname }],
    }),
    children: [
      {
        index: true,
        element: wrapElement(<TrainingTeammatePageAutomationResults />, {
          withNavigationFallback: true,
        }),
      },
    ],
  };
}

/**
 * The teammate detail subtree, shared by both route trees. A factory (rather
 * than a shared constant) because both routers include the subtree.
 */
export function createTeammatePageRoute({
  crumbs,
  wrapElement = (element) => element,
  includesAutomationResults = false,
}: {
  crumbs: BuildCrumbs;
  /** Wraps each page's element (team members wrap in their access guard). */
  wrapElement?: WrapElement;
  /** The team-member-only Notifications subtree. */
  includesAutomationResults?: boolean;
}): RouteObject {
  return {
    path: appPaths.teammate.pattern,
    handle: defineHandle({
      workspaceId: WorkspaceId.Performance,
      navigationMenu: <PerformanceNavigationMenu />,
      crumbs,
      pageContext: ({ params }) => ({
        page: "teammate",
        teammateId: params.teammateId,
      }),
    }),
    children: [
      {
        Component: TrainingTeammatePage,
        children: [
          {
            path: "",
            element: wrapElement(<TrainingTeammatePageOverview />, {
              withNavigationFallback: true,
            }),
            children: [
              createCriterionDrilldownRoute({
                path: "criteria/:criterionId",
                // Overrides the teammate page's `page`; `playbookId` and
                // `teammateId` are inherited from it, so the criterion is already
                // scoped to this teammate.
                pageContext: ({ params }) => ({
                  page: "playbookCriterion",
                  criterionId: params.criterionId,
                }),
                buildCriterionPath: (params, criterionId) =>
                  appPaths.teammateCriterionCall.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                    criterionId,
                    gradingId: requireRouteParam(params, "gradingId"),
                  }),
                buildTupleRedirectTo: (params, gradingId) =>
                  appPaths.teammateCriterionCall.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                    criterionId: requireRouteParam(params, "criterionId"),
                    gradingId,
                  }),
              }),
            ],
          },
          {
            path: "trainings/:participantId",
            element: wrapElement(<TrainingTeammatePageTraining />, {
              withNavigationFallback: false,
            }),
            handle: defineHandle({ crumbs: () => [{ label: "Training" }] }),
          },
          {
            path: appPaths.teammateCustomers.pattern,
            element: wrapElement(
              <CustomerProfileGuard>
                <CustomersPage />
              </CustomerProfileGuard>,
              { withNavigationFallback: true },
            ),
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.customers.label }],
              pageContext: () => ({ page: "customers" }),
            }),
          },
          {
            path: "customer-interactions",
            element: wrapElement(<TrainingTeammatePageCalls />, {
              withNavigationFallback: true,
            }),
            handle: defineHandle({
              crumbs: () => [{ label: performanceSections.calls.label }],
            }),
            children: [
              createGradingSlideoverRoute({
                path: ":gradingId",
                buildCriterionPath: (params, criterionId) =>
                  appPaths.teammateCallCriterion.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                    gradingId: requireRouteParam(params, "gradingId"),
                    criterionId,
                  }),
              }),
            ],
          },
          // Backwards-compatibility redirects for old by-teammate URLs.
          {
            path: "accounts",
            element: (
              <ParamsRedirect
                buildTo={(params) =>
                  appPaths.teammateCalls.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                  })
                }
              />
            ),
          },
          {
            path: "accounts/:accountId/*",
            element: (
              <GradingTupleRedirect
                buildTo={(params, gradingId) =>
                  appPaths.teammateCall.build({
                    playbookId: requireRouteParam(params, "playbookId"),
                    teammateId: requireRouteParam(params, "teammateId"),
                    gradingId,
                  })
                }
              />
            ),
          },
          {
            path: "achievements",
            element: wrapElement(<TrainingTeammatePageAchievements />, {
              withNavigationFallback: true,
            }),
            handle: defineHandle({ crumbs: () => [{ label: "Achievements" }] }),
          },
          ...(includesAutomationResults
            ? [createAutomationResultsRoute(wrapElement)]
            : []),
          // Backwards compatibility redirects for old routes
          {
            path: "playbook",
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <ParamsRedirect
                    buildTo={(params) =>
                      appPaths.teammate.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                        teammateId: requireRouteParam(params, "teammateId"),
                      })
                    }
                  />
                ),
              },
              {
                path: "criteria/:criterionId",
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: (
                      <ParamsRedirect
                        buildTo={(params) =>
                          appPaths.teammateCriterion.build({
                            playbookId: requireRouteParam(params, "playbookId"),
                            teammateId: requireRouteParam(params, "teammateId"),
                            criterionId: requireRouteParam(
                              params,
                              "criterionId",
                            ),
                          })
                        }
                      />
                    ),
                  },
                  {
                    // The old tuple URL resolves its grading directly instead of
                    // hopping through the (also legacy) `criteria/.../accounts/...`
                    // tuple URL that used to be the target here.
                    path: "accounts/:accountId",
                    element: (
                      <GradingTupleRedirect
                        buildTo={(params, gradingId) =>
                          appPaths.teammateCriterionCall.build({
                            playbookId: requireRouteParam(params, "playbookId"),
                            teammateId: requireRouteParam(params, "teammateId"),
                            criterionId: requireRouteParam(
                              params,
                              "criterionId",
                            ),
                            gradingId,
                          })
                        }
                      />
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // Full page, outside the teammate tabs.
        path: appPaths.teammateCustomer.pattern,
        element: wrapElement(
          <CustomerProfileGuard>
            <CustomerDetailPage />
          </CustomerProfileGuard>,
          { withNavigationFallback: false },
        ),
        handle: defineHandle({
          crumbs: ({ params }) => [
            {
              label: performanceSections.customers.label,
              href: appPaths.teammateCustomers.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
              }),
            },
            { label: <CustomerNameCrumbLabel accountId={params.accountId} /> },
          ],
          pageContext: ({ params }) => ({
            page: "customer",
            accountId: params.accountId,
          }),
        }),
        children: [
          createGradingSlideoverRoute({
            path: "customer-interactions/:gradingId",
            buildCriterionPath: (params, criterionId) =>
              appPaths.teammateCustomerCallCriterion.build({
                playbookId: requireRouteParam(params, "playbookId"),
                teammateId: requireRouteParam(params, "teammateId"),
                accountId: requireRouteParam(params, "accountId"),
                gradingId: requireRouteParam(params, "gradingId"),
                criterionId,
              }),
          }),
        ],
      },
    ],
  };
}
